/**
 * Fleet Management Domain Types
 * Aligned with svc-asset backend DTOs
 */

// ==================== DRIVER ====================

// Backend: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED'
// Frontend-friendly mapping for UI
export type DriverStatusBackend = 'ACTIVE' | 'INACTIVE' | 'SUSPENDED'
export type DriverStatus = 'available' | 'assigned' | 'on-leave' | 'inactive'

// Status mapping helpers
export const driverStatusToBackend: Record<DriverStatus, DriverStatusBackend> = {
  available: 'ACTIVE',
  assigned: 'ACTIVE',
  'on-leave': 'SUSPENDED',
  inactive: 'INACTIVE',
}

export const driverStatusFromBackend: Record<DriverStatusBackend, DriverStatus> = {
  ACTIVE: 'available',
  INACTIVE: 'inactive',
  SUSPENDED: 'on-leave',
}

export type LicenseType = 'heavy' | 'light' | 'hazmat' | 'special'

export type DriverPreferredLanguage = 'en' | 'ar' | 'ur'
export const DRIVER_PREFERRED_LANGUAGES = ['en', 'ar', 'ur'] as const

export interface Driver {
  id: string
  documentId?: string // Strapi documentId
  tenantId: string // Backend required
  /** Tenant info (populated for admin views) */
  tenant?: { name: string; code: string }
  name: string
  nameAr?: string // Frontend addition - could be in metadata
  driverId?: string // Backend field
  licenseNumber: string
  licenseType: LicenseType | string // Backend accepts any string
  licenseExpiry: string
  nationality?: string // Backend field
  phone?: string
  whatsappNumber?: string
  preferredLanguage?: DriverPreferredLanguage
  status: DriverStatus
  photo?: string // Frontend addition - in metadata
  metadata?: Record<string, unknown> // Backend flexible storage
  createdAt: string
  updatedAt: string
  deletedAt?: string | null
}

// Backend CreateDriverDto
export interface CreateDriverDto {
  nameAr?: string
  driverId?: string // Backend field
  photo?: string
  tenantId: string
  name: string
  licenseNumber: string
  licenseType: string
  licenseExpiry: string
  nationality?: string
  phone?: string
  whatsappNumber?: string
  preferredLanguage?: DriverPreferredLanguage
  status?: DriverStatusBackend
  metadata?: Record<string, unknown>
}

// ==================== VEHICLE / ASSET ====================

// Backend: 'active' | 'inactive' | 'maintenance' | 'retired' | 'disposed'
export type VehicleStatusBackend = 'active' | 'inactive' | 'maintenance' | 'retired' | 'disposed'
export type VehicleStatus = 'available' | 'assigned' | 'maintenance' | 'out-of-service'

export const vehicleStatusToBackend: Record<VehicleStatus, VehicleStatusBackend> = {
  available: 'active',
  assigned: 'active',
  maintenance: 'maintenance',
  'out-of-service': 'retired',
}

export const vehicleStatusFromBackend: Record<VehicleStatusBackend, VehicleStatus> = {
  active: 'available',
  inactive: 'out-of-service',
  maintenance: 'maintenance',
  retired: 'out-of-service',
  disposed: 'out-of-service',
}

export type VehicleType = 'truck' | 'pump' | 'equipment' | 'mixer' | 'tanker' | 'flatbed' | 'crane' | 'loader'

// Backend: OWNED, LEASED_IN, RENTED_OUT, OUTSOURCED, BROKERED, SHARED
export type OwnershipTypeBackend = 'OWNED' | 'LEASED_IN' | 'RENTED_OUT' | 'OUTSOURCED' | 'BROKERED' | 'SHARED'
export type OwnershipType = 'owned' | 'leased' | 'rented-out' | 'outsourced' | 'brokered' | 'shared'

export const ownershipToBackend: Record<OwnershipType, OwnershipTypeBackend> = {
  owned: 'OWNED',
  leased: 'LEASED_IN',
  'rented-out': 'RENTED_OUT',
  outsourced: 'OUTSOURCED',
  brokered: 'BROKERED',
  shared: 'SHARED',
}

export const ownershipFromBackend: Record<OwnershipTypeBackend, OwnershipType> = {
  OWNED: 'owned',
  LEASED_IN: 'leased',
  RENTED_OUT: 'rented-out',
  OUTSOURCED: 'outsourced',
  BROKERED: 'brokered',
  SHARED: 'shared',
}

export interface Vehicle {
  assetNumber: string
  name: { en: string; ar: string }
  manufacturer?: string
  currentProjectId?: string
  currentProjectName?: string
  driverName?: string
  driverId?: string
  location?: { lat: number; lng: number }
  id: string
  documentId?: string // Strapi documentId
  tenantId: string // Backend required
  /** Tenant info (populated for admin views) */
  tenant?: { name: string; code: string }
  category: string // Backend required (e.g., 'VEHICLE', 'TRUCK')
  plateNumber?: string
  vin?: string // Backend field
  serialNumber?: string // Backend field
  type: VehicleType // Frontend - stored in metadata
  make: string // Frontend - stored in metadata
  model: string // Frontend - stored in metadata
  year?: number
    capacity: { value: number; unit: 'tons' | 'm³' | 'kg' } // Frontend - in metadata
  status: VehicleStatus
  ownership: OwnershipType
  lastMaintenance?: string // Frontend - in metadata
  nextMaintenance?: string // Frontend - in metadata
  metadata?: Record<string, unknown> // Backend flexible storage
  createdAt: string
  updatedAt: string
}

// Backend CreateAssetDto
export interface CreateAssetDto {
  type?: VehicleType
  make?: string
  model?: string
  capacity?: { value: number; unit: "tons" | "m³" | "kg" }
  lastMaintenance?: string
  nextMaintenance?: string
  ownership?: OwnershipType
  tenantId: string
  category: string
  ownershipType: OwnershipTypeBackend
  year?: number
  plateNumber?: string
  vin?: string
  serialNumber?: string
  status?: VehicleStatusBackend
  metadata?: Record<string, unknown>
}

// ==================== TRAILER ====================

// Trailer uses same status as Vehicle in backend (it's an Asset with category='TRAILER')
export type TrailerType = "trailer" | "flatbed" | "tanker"
export type TrailerStatus = 'available' | 'assigned' | 'maintenance' | 'out-of-service'

export interface Trailer {
  assetNumber: string
  name?: { en: string; ar: string }
  id: string
  documentId?: string // Strapi documentId
  tenantId: string
  /** Tenant info (populated for admin views) */
  tenant?: { name: string; code: string }
  category: string // 'TRAILER'
  plateNumber?: string
    capacity: { value: number; unit: 'tons' | 'm³' } // in metadata
  status: TrailerStatus
  metadata?: Record<string, unknown>
  createdAt: string
  updatedAt: string
}

// ==================== RESOURCE ====================

// Backend: 'ACTIVE' | 'INACTIVE'
export type ResourceStatusBackend = 'ACTIVE' | 'INACTIVE'
export type ResourceStatus = 'active' | 'inactive'

export const resourceStatusToBackend: Record<ResourceStatus, ResourceStatusBackend> = {
  active: 'ACTIVE',
  inactive: 'INACTIVE',
}

export const resourceStatusFromBackend: Record<ResourceStatusBackend, ResourceStatus> = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
}

export interface Resource {
  id: string
  documentId?: string // Strapi documentId
  tenantId: string // Backend required
  /** Tenant info (populated for admin views) */
  tenant?: { name: string; code: string }
  name: string // Backend field
  description?: string // Backend field
  driverId?: string
  driver?: Driver // Frontend embedded
  vehicleId?: string // Frontend name
  powerUnitId?: string // Backend name (alias for vehicleId)
  vehicle?: Vehicle // Frontend embedded
  trailerId?: string
  trailer?: Trailer // Frontend embedded
  status: ResourceStatus
  assignedSince?: string // Frontend - in metadata
  currentShipmentId?: string // Frontend - in metadata
  metadata?: Record<string, unknown>
  createdAt: string
  updatedAt: string
  deletedAt?: string | null
}

// Backend CreateResourceDto
export interface CreateResourceDto {
  assignedSince?: string
  currentShipmentId?: string
  vehicleId?: string
  tenantId: string
  name: string
  description?: string
  powerUnitId?: string // vehicleId
  trailerId?: string
  driverId?: string
  status?: ResourceStatusBackend
  metadata?: Record<string, unknown>
}

// ==================== STATS ====================

export interface FleetStats {
  totalDrivers: number
  availableDrivers: number
  totalVehicles: number
  availableVehicles: number
  totalResources: number
  activeResources: number
  onDelivery: number
}

// ==================== SORTING ====================

export type SortOrder = 'asc' | 'desc'

// Sort field types for each entity
export type DriversSortBy = 'name' | 'licenseExpiry' | 'status' | 'createdAt'
export type VehiclesSortBy = 'plateNumber' | 'make' | 'capacity' | 'status' | 'ownership' | 'createdAt'
export type ResourcesSortBy = 'name' | 'status' | 'assignedSince' | 'createdAt'

// Default sort configuration
export const DEFAULT_DRIVERS_SORT = { sortBy: 'name' as DriversSortBy, sortOrder: 'asc' as SortOrder }
export const DEFAULT_VEHICLES_SORT = { sortBy: 'plateNumber' as VehiclesSortBy, sortOrder: 'asc' as SortOrder }
export const DEFAULT_RESOURCES_SORT = { sortBy: 'name' as ResourcesSortBy, sortOrder: 'asc' as SortOrder }

// ==================== FILTERS ====================

export interface DriversFilter {
  status?: DriverStatus | DriverStatus[] | 'all'
  licenseType?: LicenseType | LicenseType[] | 'all'
  name?: string // Search by name
  phone?: string // Search by phone
  employeeNumber?: string // Search by employee/driver number
  isBinded?: boolean | 'all' // Filter by whether driver is assigned to a vehicle
  search?: string
  sortBy?: DriversSortBy
  sortOrder?: SortOrder
  page?: number
  pageSize?: number
}

export interface VehiclesFilter {
  status?: VehicleStatus | VehicleStatus[] | 'all'
  type?: VehicleType | VehicleType[] | 'all'
  ownership?: OwnershipType | OwnershipType[] | 'all'
  licensePlate?: string // Search by license plate
  isBinded?: boolean | 'all' // Filter by whether vehicle is assigned to a resource
  search?: string
  sortBy?: VehiclesSortBy
  sortOrder?: SortOrder
  page?: number
  pageSize?: number
}

export interface TrailersFilter {
  status?: TrailerStatus | 'all'
  type?: string | 'all'
  search?: string
  page?: number
  pageSize?: number
}

export interface ResourcesFilter {
  status?: ResourceStatus | ResourceStatus[] | 'all'
  driverName?: string // Autocomplete search by driver name
  vehicleLicensePlate?: string // Search by vehicle license plate
  driverPhone?: string // Search by driver phone
  search?: string
  sortBy?: ResourcesSortBy
  sortOrder?: SortOrder
  page?: number
  pageSize?: number
  /** Scope results to a specific tenant (by documentId). Prevents cross-tenant data leakage in admin portal. */
  tenantDocumentId?: string | null
  /** Filter resources by powerUnit's metadata.vehicleType (e.g. 'mixer', 'flatbed') */
  powerUnitVehicleType?: string
}

// ==================== LEGACY (backwards compatibility) ====================

export type AssetStatus = VehicleStatus
export type AssetOwnership = OwnershipType
export type AssetType = VehicleType
export type Asset = Vehicle

export interface AssetStats {
  active: number
  maintenance: number
  idle: number
  total: number
  owned: number
  leased: number
}

export interface AssetsFilter {
  status?: AssetStatus | 'all'
  type?: AssetType | 'all'
  ownership?: AssetOwnership | 'all'
  search?: string
  page?: number
  pageSize?: number
}

// ==================== PAGINATION ====================

export const DEFAULT_PAGE_SIZE = 10
export const MAX_PAGE_SIZE = 1000

export interface PaginationMeta {
  page: number
  pageSize: number
  pageCount: number
  total: number
}

export interface PaginatedVehicles {
  data: Vehicle[]
  pagination: PaginationMeta
}

export interface PaginatedDrivers {
  data: Driver[]
  pagination: PaginationMeta
}

export interface PaginatedTrailers {
  data: Trailer[]
  pagination: PaginationMeta
}

export interface PaginatedResources {
  data: Resource[]
  pagination: PaginationMeta
}
